import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { DateNav } from "../shell/DateNav";
import { PageHeader } from "../shell/PageHeader";
import { useHasLens } from "../session/capabilities";
import type { Period } from "../router/search";
import {
  useGetV1Kpi,
  type EnvelopeKpiData,
  type Figure,
  type LossCascadeRow,
  type RowStatus,
} from "@solarkey/api-client";
import { basisLabel, ErrorState, figuresAtOneScale, Metric, Money, Skeleton,
         StateWord } from "@solarkey/ui";

// The sibling screen 17 renders the same cascade at site scope and is already audited;
// its row naming, its "which engine is missing" reader and its server-period label are
// imported rather than re-implemented, so both screens read the same rows the same way.
import { displayLabel, engineOf, periodLabel } from "./SitePerformance";

/* ── Screen 18 (F3): Loss waterfall at portfolio scope, with a date range ──
   The 17-row loss taxonomy (SKY-NMH-LOSS-001 v1.1) at fleet scale. Every figure on this
   screen comes from `/v1/kpi` at the screen's scope — the cascade rows, their values,
   their bases and their provenance. Nothing is derived here: shares, the cascade
   headline, closure and any per-site split belong to the engine, never the browser
   (CLAUDE.md rule 2; KPIDEF-001 §3.2/6.1/9).

   Every control Disha built stays live. Where the frozen v1 contract serves no figure for
   a control, the control still works and the one figure inside it prints its state word
   with the true reason — never a zero, never a dash:
     · Share column            — /v1/kpi carries no per-row share of potential.
     · Cascade headline        — no "% of potential exported" / "lost" figure is served.
     · Track axis              — no share-of-potential scale is served, so the track is
                                 scaled by magnitude (the audited rule of the shared
                                 LossCascade component), not by share.
     · By-site drill           — no per-site split of a cascade row is served; the panel
                                 states the served fleet aggregate for the selected row
                                 and names what is missing.
     · Sites in this waterfall — no site count or coverage count travels with the cascade.
     · Soiling supplied        — no per-site soiling coverage count is served. */

// Period control, as wireframe 18 draws it. `custom` is a real window here: the date
// navigator beside these tabs sets `from`/`to`, and until it has, the screen says in
// words that the server resolved its own — the default is never silent (rule 6).
const PERIOD_TABS: { key: Period; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "mtd", label: "Month" },
  { key: "fy", label: "FY" },
  { key: "custom", label: "Custom" },
];

/* The anchor (level) rows of the taxonomy, as against the step (band) rows. This is the
   shape of SKY-NMH-LOSS-001, not data: it decides whether a row draws a level tick or a
   band, and whether it reads as a total. */
const LEVEL_ROWS = new Set([
  "plan",
  "plan_weather_adjusted",
  "potential",
  "available",
  "generated",
  "exported",
]);

/** The residual row, drawn hatched (chart spec §5.6). */
const RESIDUAL_ROW = "unattributed";

/** The ruled row status, in plain words (UX-003 §3.1). */
const ROW_STATUS_LABEL: Record<string, string> = {
  engine: "engine",
  platform: "platform",
  platform_estimate: "platform estimate",
  NOT_MEASURED: "not measured",
};

/* Plain-language reading of the taxonomy rows. This is the glossary copy of
   SKY-NMH-LOSS-001 v1.1 — definitions, not measurements. No figure appears here. */
const READING: [string, string][] = [
  ["Plan", "what was promised for the period (P50)"],
  ["Weather", "sun above or below forecast"],
  ["Plan, weather-adjusted", "the promise adjusted for the sun"],
  ["Plan allowance", "losses the plan already budgeted"],
  ["Potential", "what a perfect plant makes under this sun"],
  ["Grid outage", "the grid was off"],
  ["Grid curtailment", "the grid asked us to reduce"],
  ["Downtime", "equipment was off, by cause"],
  ["Available", "what the running equipment could make"],
  ["Soiling", "dirt on the panels (reversible)"],
  ["Array", "panels on but weak (DC side)"],
  ["Conversion", "inverter losses (AC side)"],
  ["Generated", "what the inverters made"],
  ["Auxiliary", "the plant's own use"],
  ["Evacuation", "lost between inverter and meter"],
  ["Unexplained", "not yet accounted for (the closure residual)"],
  ["Exported", "what the meter recorded"],
];

/** A headline figure, or its state word. The strip used to print a state word
 *  unconditionally, on a note saying /v1/kpi does not carry it — it does, as
 *  `cascade_headline`, derived server-side from the engine's own anchors. */
function HeadlineFigure({ figure }: { figure?: Figure }): React.ReactNode {
  if (!figure) {
    return <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_CASCADE_HEADLINE" }} />;
  }
  return <Metric figure={figure} showBasis={false} showProvenance={false}
                 showFreshness={false} maximumFractionDigits={1} />;
}

/** The share axis the wireframe draws: the GAP only, not 0–100.
 *
 *  Every row is a share of potential, and on a working plant they all crowd the
 *  top of the range — a 0–100 axis would stack every tick on the right-hand edge
 *  and show nothing. So the axis starts just below the lowest anchor, rounded
 *  down to a whole step, exactly as the drawing's "94 96 98 100%" does.
 *
 *  Null when no anchor carries a share: then there is no axis to draw, and the
 *  track falls back to relative size. */
export function shareAxis(shares: readonly (number | null)[]): { lo: number; hi: number } | null {
  const known = shares.filter((s): s is number => typeof s === "number");
  if (known.length === 0) return null;
  const hi = Math.max(100, Math.ceil(Math.max(...known)));
  const lowest = Math.min(...known, 100);
  // A step of 2 points, like the drawing, and never a zero-width axis.
  const lo = Math.max(0, Math.min(Math.floor((lowest - 1) / 2) * 2, hi - 4));
  return { lo, hi };
}

/** Where each row's mark sits on the share axis.
 *
 *  An anchor is a tick at its own share. A band is a step away from the anchor
 *  above it — to the left when it is a loss, to the right when it is a gain —
 *  so the bar's two ends are both served shares. A row with no share, or a
 *  cascade with no axis, gets nothing here and the cell falls back. */
export function buildTrack(
  rows: readonly LossCascadeRow[], scale: { lo: number; hi: number } | null,
): ({ tick: boolean; left: number; width: number } | null)[] {
  if (!scale) return rows.map(() => null);
  const span = scale.hi - scale.lo || 1;
  const at = (s: number) => Math.max(0, Math.min(100, ((s - scale.lo) / span) * 100));
  let levelAbove = scale.hi;
  return rows.map((r) => {
    const share = typeof r.share_of_potential?.value === "number"
      ? r.share_of_potential.value : null;
    const isLevel = !!r.label && LEVEL_ROWS.has(r.label);
    if (share === null) return null;
    if (isLevel) {
      levelAbove = share;
      return { tick: true, left: at(share), width: 0 };
    }
    const [from_, to_] = share >= 0
      ? [levelAbove - share, levelAbove]
      : [levelAbove, levelAbove - share];
    // A hairline so a step too small to see still says it is there.
    return { tick: false, left: at(from_), width: Math.max(0.6, at(to_) - at(from_)) };
  });
}

export function PortfolioWaterfall({ scope, period, from, to }: {
  scope?: string; period?: string; from?: string; to?: string;
}): React.ReactNode {
  const navigate = useNavigate();
  const activePeriod = (period ?? "30d") as Period;
  // A custom window IS requestable: `/v1/kpi` has taken `from`/`to` since the
  // contract gained `periodFrom`/`periodTo`, and the generated client sends them.
  // This screen simply never passed them, so every custom range fell through to
  // the live day — which on a lagging feed is the one day with no cascade at all.
  const windowArgs = activePeriod === "custom" ? { from, to } : {};
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const hasValueLens = useHasLens("value");

  // One query for the screen. `scope` undefined means the person's full scope — the
  // portfolio — which is exactly what this screen is (openapi `scope` parameter).
  const base = useGetV1Kpi({ scope, period: activePeriod, ...windowArgs });

  const setPeriod = (key: Period) =>
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      // Leaving a custom window drops its bounds: they belong to that window only.
      ...prev, period: key, ...(key === "custom" ? {} : { from: undefined, to: undefined }),
    })) as never });

  /** The navigator stepped or picked a window: that IS a custom range. */
  const setWindow = (f: string, t: string) =>
    void navigate({ search: ((prev: Record<string, unknown>) => ({
      ...prev, period: "custom", from: f, to: t,
    })) as never });

  if (base.isLoading) {
    return (
      <div className="flex flex-col gap-[14px]" aria-busy="true">
        <Skeleton variant="text" width="16rem" />
        <Skeleton variant="row" lines={10} />
      </div>
    );
  }

  if (base.isError || !base.data || base.data.status !== 200) {
    return (
      <ErrorState
        title="The loss waterfall is unavailable"
        message="The portfolio KPI view could not be loaded. Nothing is shown rather than stale or partial figures."
        onRetry={() => void base.refetch()}
      />
    );
  }

  const env = base.data.data;
  const d: EnvelopeKpiData = env.data;
  // ONE unit for the whole cascade, decided by its largest row. The rows are steps
  // of the same energy, so a column that mixes MWh and kWh makes the steps
  // incomparable — which is all a cascade is for. Scaling here means every
  // reader of `rows` below, the by-site panel included, is already on that scale.
  const delivered: LossCascadeRow[] = d.loss_cascade ?? [];
  const oneScale = figuresAtOneScale(delivered.map((r) => r.figure));
  const rows: LossCascadeRow[] = delivered.map((r, i) => ({ ...r, figure: oneScale.figures[i] }));
  // The window the server resolved, never the browser clock: the backend anchors every
  // period to the latest data day, and a lagging feed must not be printed as today.
  const periodLine = periodLabel(env.meta?.period);
  // What the SERVER resolved, which the navigator shows instead of a window the
  // browser clock would work out: the backend anchors to the latest computed day.
  const resolvedWindow = env.meta?.period as { start?: string; end?: string } | undefined;
  const unit = oneScale.unit;

  // Chart spec §5.6: every band is a share of Potential, so with no Potential figure
  // there is no waterfall to draw at all.
  const potential = rows.find((r) => r.label === "potential");
  const hasPotential = typeof potential?.figure?.value === "number";

  // Track scaling. The contract serves no share-of-potential axis, so the track is
  // scaled by magnitude — the same rule the shared LossCascade component applies. This
  // is chart geometry over served values; no share or closure is computed or stated.
  const maxAbs = Math.max(
    1,
    ...rows.map((r) => (typeof r.figure?.value === "number" ? Math.abs(r.figure.value) : 0)),
  );

  const headline = d.cascade_headline;
  // The axis is built from the ANCHOR shares only: those are the levels the
  // cascade steps between, and a band's share is a step size, not a position.
  const shareScale = shareAxis(
    rows.filter((r) => r.label && LEVEL_ROWS.has(r.label))
        .map((r) => (typeof r.share_of_potential?.value === "number"
                     ? r.share_of_potential.value : null)),
  );
  const axisTicks = shareScale
    ? [0, 1, 2, 3].map((k) => shareScale.lo + ((shareScale.hi - shareScale.lo) * k) / 3)
    : [];

  // The track, laid out once. A band steps away from the level above it: geometry
  // over two SERVED shares, never a running total of the browser's own — a walk
  // that invented the level after each band would contradict the next anchor
  // wherever a band is NOT_MEASURED, which is most of them today.
  const track = buildTrack(rows, shareScale);

  const selectedLabel = selectedRow ?? rows.find((r) => r.label === "downtime")?.label ?? rows[0]?.label ?? null;
  const selectedCascadeRow = rows.find((r) => r.label === selectedLabel);

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        title="Performance"
        scope={periodLine}
        timeControl="none"
        family="performance"
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div className="sk-scenario" role="group" aria-label="Time period">
              {PERIOD_TABS.map((t) => (
                <button key={t.key} type="button" data-active={activePeriod === t.key ? "true" : "false"} onClick={() => setPeriod(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
            {/* Was a decorative span: two aria-hidden chevrons that were never
                buttons, around a label. It could not step a window because it
                was not a control. This is the same navigator Site Performance
                uses — arrows step, the label opens a From/To picker, forward
                stops at the latest day the server has served. */}
            <DateNav
              period={activePeriod}
              from={from}
              to={to}
              resolved={{ from: resolvedWindow?.start, to: resolvedWindow?.end }}
              onNavigate={setWindow}
            />
            <button type="button" style={exportBtnStyle} onClick={() => exportCsv(periodLine, d, rows)}>
              Export
            </button>
          </div>
        }
      />

      {activePeriod === "custom" && !(from && to) ? (
        <p className="sk-sp-note">
          No custom window is chosen yet, so the server resolved its own. Step with the
          arrows or open the date label to pick a From and To.
        </p>
      ) : null}

      <div className="sk-pw-cols">
        {/* ── From plan to exported: the 17-row waterfall ── */}
        <section aria-labelledby="pw-wf" className="sk-section">
          <div className="sk-section__head" style={{ flexWrap: "wrap" }}>
            <div>
              <h2 id="pw-wf" className="sk-section__title" style={{ whiteSpace: "nowrap" }}>From plan to exported</h2>
              <span className="sk-section__sub">{periodLine} · as delivered by the engine</span>
            </div>
            {/* The headline the drawing prints above the rows. `/v1/kpi` serves it as
                `cascade_headline` — both figures derived server-side from the engine's
                own anchors — so it is read, never re-derived here. */}
            <span className="sk-section__sub">
              <span>Share of potential exported: </span>
              <HeadlineFigure figure={headline?.exported_share} />
              <span> · lost: </span>
              <HeadlineFigure figure={headline?.energy_lost} />
            </span>
          </div>

          {rows.length === 0 ? (
            <>
              <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
              <p className="sk-sp-note">
                The daily ladder produced no rows for this scope and window, so there is no
                cascade to state.
              </p>
            </>
          ) : !hasPotential ? (
            <>
              <StateWord
                state={potential?.figure?.state ?? "NOT_MEASURED"}
                reason={potential?.figure?.reason ?? { template: "NOT_MEASURED_ENGINE_ABSENT" }}
              />
              <p className="sk-sp-note">
                Potential carries no figure, so there is no waterfall: every band is a share of
                Potential, and without it no step can be placed (chart spec §5.6).
                {engineOf(potential) ? ` Blocked on the ${engineOf(potential)} engine.` : null}
              </p>
            </>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="sk-lw-table sk-lw-table--compact">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th style={{ width: 160 }}>
                      {shareScale ? (
                        <div className="sk-lw-scale" aria-hidden>
                          {axisTicks.map((v, k) => (
                            <span key={k} style={{ left: `${(k / 3) * 100}%` }}>
                              {Math.round(v)}{k === axisTicks.length - 1 ? "%" : ""}
                            </span>
                          ))}
                        </div>
                      ) : "Relative size"}
                    </th>
                    {/* The column heads the figures' own unit — never a hardcoded one. */}
                    <th className="sk-lw-num">{unit}</th>
                    <th className="sk-lw-num">Share</th>
                    <th>Supplied by</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const label = displayLabel(r.label);
                    const isLevel = !!r.label && LEVEL_ROWS.has(r.label);
                    const isSel = selectedLabel === r.label;
                    const value = typeof r.figure?.value === "number" ? r.figure.value : null;
                    const pct = value === null ? null : (Math.abs(value) / maxAbs) * 100;
                    const bar = track[i];
                    return (
                      <tr
                        key={`${i}-${r.label ?? ""}`}
                        className={`${isLevel ? "sk-lw-tot" : ""}${isSel ? " sk-lw-sel" : ""} sk-lw-drill`}
                        onClick={() => setSelectedRow(r.label ?? null)}
                        aria-expanded={isSel}
                      >
                        <td style={{ whiteSpace: "nowrap" }}>
                          <span className="sk-lw-dis">{isSel ? "▾" : "▸"}</span>
                          {label}
                        </td>
                        <td>
                          {/* With shares served, this is the drawing's track: an anchor
                              ticks at its own level on the share axis, and a band is the
                              step away from the level above it — left for a loss, right
                              for a gain. With no share, it falls back to magnitude. */}
                          <div className="sk-lw-trk">
                            {bar ? (
                              bar.tick ? (
                                <b style={{ left: `${bar.left}%` }} />
                              ) : (
                                <i className={r.label === RESIDUAL_ROW ? "un" : ""}
                                   style={{ left: `${bar.left}%`, width: `${bar.width}%` }} />
                              )
                            ) : shareScale ? null : pct === null ? null : isLevel ? (
                              <b style={{ left: `${pct}%` }} />
                            ) : (
                              <i className={r.label === RESIDUAL_ROW ? "un" : ""} style={{ left: 0, width: `${pct}%` }} />
                            )}
                          </div>
                        </td>
                        <td className="sk-lw-num">
                          {/* The energy figure, always — this column is headed with the
                              energy unit. `money` is served on every row with a null
                              amount and state CANNOT_CHECK, so substituting it under the
                              Value lens replaced every figure on the screen with "Cannot
                              check" under an MWh heading. Money is an ADDITION, and only
                              when it carries an amount. */}
                          {r.figure ? (
                            <Metric figure={r.figure} showBasis={false} showProvenance={false}
                                    showFreshness={false}
                                    maximumFractionDigits={3} minimumFractionDigits={3} />
                          ) : (
                            <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
                          )}
                          {hasValueLens && typeof r.money?.amount === "number" ? (
                            <div className="sk-lw-muted"><Money money={r.money} /></div>
                          ) : null}
                        </td>
                        <td className="sk-lw-num">
                          {/* Served per row as `share_of_potential`, computed server-side
                              from two engine figures so the browser never derives a ruled
                              one. Withheld with POTENTIAL, as the contract says. */}
                          {r.share_of_potential ? (
                            <Metric figure={r.share_of_potential} showBasis={false}
                                    showProvenance={false} showFreshness={false}
                                    maximumFractionDigits={1} minimumFractionDigits={1} />
                          ) : (
                            <StateWord state="NOT_MEASURED"
                                       reason={{ template: "NOT_MEASURED_NO_CASCADE_ROW_SHARE" }} />
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <RowSupply row={r} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="sk-sp-note">
            {shareScale
              ? `Track: the gap only, ${shareScale.lo} to ${shareScale.hi} percent of potential. Anchors are ticks at their level; bands are steps, losses to the left and gains to the right.`
              : "Track: relative size of each row, scaled to the largest row in the cascade. No share of potential is served for this window, so the wireframe's share scale is not drawn."}
          </p>

          {/* ── The strip of small facts about the cascade ── */}
          <div className="sk-kvstrip" style={{ marginTop: 10 }}>
            <KvItem
              label="Weather, against plan"
              figure={rows.find((r) => r.label === "weather")?.figure}
              sub="the Weather row, as the engine delivered it"
            />
            <KvItem
              label="The plants, against plan"
              figure={d.attainment_ratio}
              sub="attainment against plan, as served"
            />
            <div className="sk-kvstrip__item">
              <span className="sk-kvstrip__label">Sites in this waterfall</span>
              <span className="sk-kvstrip__val">
                <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_CASCADE_SITE_COUNT" }} />
              </span>
              <span className="sk-kvstrip__sub">no site count travels with the cascade</span>
            </div>
            <div className="sk-kvstrip__item">
              <span className="sk-kvstrip__label">Soiling supplied</span>
              <span className="sk-kvstrip__val">
                <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_CASCADE_SITE_COUNT" }} />
              </span>
              <span className="sk-kvstrip__sub">no per-site soiling coverage is served</span>
            </div>
          </div>
        </section>

        {/* ── Right column: by-site drill + reading the rows ── */}
        <div className="flex flex-col gap-[14px]">
          <section aria-labelledby="pw-bysite" className="sk-section">
            <div className="sk-section__head">
              <div>
                <h2 id="pw-bysite" className="sk-section__title">{displayLabel(selectedLabel ?? undefined)}, by site</h2>
                <span className="sk-section__sub">the selected row</span>
              </div>
            </div>
            <table className="sk-lw-table sk-lw-table--compact">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="sk-lw-num">{`Lost ${unit}`}</th>
                  <th className="sk-lw-num">Of the row %</th>
                  <th>Inside it</th>
                </tr>
              </thead>
              <tbody>
                {/* The one real figure this panel has: the fleet aggregate the engine
                    delivered for the selected row. */}
                <tr>
                  <td><b>All sites</b></td>
                  <td className="sk-lw-num">
                    {selectedCascadeRow?.figure ? (
                      <Metric
                        figure={selectedCascadeRow.figure}
                        showBasis={false}
                        showProvenance={false}
                        showFreshness={false}
                        maximumFractionDigits={3}
                        minimumFractionDigits={3}
                      />
                    ) : (
                      <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
                    )}
                  </td>
                  <td className="sk-lw-num">
                    <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_NO_CASCADE_ROW_SHARE" }} />
                  </td>
                  <td>the served fleet aggregate</td>
                </tr>
                <tr>
                  <td colSpan={4}>
                    <StateWord state="CANNOT_CHECK" reason={{ template: "CANNOT_CHECK_NO_CASCADE_SITE_SPLIT" }} />
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} className="sk-lw-muted">
                    No per-site split of a cascade row is served at portfolio scope, so the sites
                    behind this row cannot be named or ranked. Splitting the aggregate here would
                    invent the ranking.
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section aria-labelledby="pw-reading" className="sk-section">
            <div className="sk-section__head">
              <div>
                <h2 id="pw-reading" className="sk-section__title">Reading the rows</h2>
                <span className="sk-section__sub">plain language, from the taxonomy</span>
              </div>
            </div>
            <div className="sk-pw-reading">
              {READING.map(([label, desc]) => (
                <div key={label}><b>{label}</b> · {desc}</div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ── One fact in the strip: a server figure, or its state word ── */
function KvItem(props: { label: string; figure?: Figure; sub: string }): React.ReactNode {
  return (
    <div className="sk-kvstrip__item">
      <span className="sk-kvstrip__label">{props.label}</span>
      <span className="sk-kvstrip__val">
        {props.figure ? (
          <Metric figure={props.figure} showBasis={false} showProvenance={false} showFreshness={false} />
        ) : (
          <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
        )}
      </span>
      <span className="sk-kvstrip__sub">{props.sub}</span>
    </div>
  );
}

/* ── Who supplied a row: its ruled status, its basis, and the engine it waits on ── */
function RowSupply({ row }: { row: LossCascadeRow }): React.ReactNode {
  const status = row.row_status as RowStatus | undefined;
  const basis = basisLabel(row.figure?.basis);
  const missing = engineOf(row);
  return (
    <>
      {status ? (
        <span className="sk-chip sk-chip--prov">{ROW_STATUS_LABEL[status] ?? status}</span>
      ) : (
        <StateWord state="NOT_MEASURED" reason={{ template: "NOT_MEASURED_ENGINE_ABSENT" }} />
      )}
      {basis ? <> <span className="sk-chip sk-chip--basis">{basis}</span></> : null}
      {missing ? <span className="sk-lw-owner"> {missing}</span> : null}
    </>
  );
}

/** Download the waterfall as a CSV. A withheld figure exports its state word, never a 0. */
function exportCsv(periodLine: string, d: EnvelopeKpiData, rows: LossCascadeRow[]): void {
  const num = (f?: Figure) => (typeof f?.value === "number" ? String(f.value) : "");
  const state = (f?: Figure) => (f ? f.state ?? "" : "CANNOT_CHECK");
  const csvRows: string[][] = [
    ["Portfolio · loss waterfall", periodLine],
    [],
    ["Figure", "Value", "Unit", "State"],
    ["Plan", num(d.plan), d.plan?.unit ?? "", state(d.plan)],
    ["Attainment against plan", num(d.attainment_ratio), d.attainment_ratio?.unit ?? "", state(d.attainment_ratio)],
    ["Energy delivered", num(d.energy_delivered), d.energy_delivered?.unit ?? "", state(d.energy_delivered)],
    [],
    ["Cascade row", "Value", "Unit", "State", "Row status", "Share of potential", "Missing engine"],
    ...rows.map((r) => [
      displayLabel(r.label),
      num(r.figure),
      r.figure?.unit ?? "",
      state(r.figure),
      String(r.row_status ?? ""),
      // Not served, and not re-derived here (CLAUDE.md rule 2).
      "NOT_MEASURED",
      engineOf(r) ?? "",
    ]),
  ];
  const csv = csvRows.map((r) => r.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "portfolio-loss-waterfall.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

const exportBtnStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", height: 30, padding: "0 12px",
  fontSize: "12px", fontWeight: 600, border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)", background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)", cursor: "pointer", whiteSpace: "nowrap",
};
